import { useState } from 'react';
import { useReduxState } from 'hooks';
import { useApiData } from 'lib/api';
import { EmptyIndicator, Pagination } from 'components/common';
import { Box, BoxRow } from 'components/common/box';
import { FeedbackCourseFilter, FeedbackItem, FeedbackUnread } from '.';

import type { Course } from 'shared-types';
import type { GetFeedbacksRes } from 'shared-types/responses';

interface Props
  extends Partial<{
    courseId: number;
    courses: Pick<Course, 'id' | 'title'>[];
    emptyDescription: string;
    isAdmin: boolean;
    showCourse: boolean;
    showUnread: boolean;
    titleText: string;
    userId: number;
  }> {}

const FeedbackContainer = ({
  courses = [],
  courseId,
  emptyDescription,
  isAdmin,
  showCourse,
  showUnread,
  titleText,
  userId
}: Props) => {
  const { messages } = useReduxState();

  const [courseIdValue, setCourseIdValue] = useState(courseId);
  const [page, setPage] = useState(1);

  const { data, loading, updateData } = useApiData<GetFeedbacksRes>({
    url:
      `/admin/feedbacks?page=${page}` +
      (courseIdValue ? `&CourseId=${courseIdValue}` : '') +
      (userId ? `&UserId=${userId}` : '')
  });

  return (
    <div className="feedback-container">
      <Box
        headerContent={
          courses?.length ? (
            <FeedbackCourseFilter
              courses={courses}
              onCourseChange={setCourseIdValue}
            />
          ) : null
        }
        headerContentWrap={true}
        headerStyle={
          isAdmin && data?.unreadCount ? { borderBottom: 'none' } : null
        }
        loading={loading}
        padding={0}
        subHeaderContent={
          showUnread && data?.unreadCount ? (
            <FeedbackUnread
              feedbacks={data.feedbacks}
              unreadCount={data.unreadCount}
              updateFeedbacks={updateData}
            />
          ) : null
        }
        title={titleText || messages.feedback}
      >
        {data?.feedbacks?.length ? (
          data?.feedbacks.map((feedback) => (
            <BoxRow key={feedback.id} padding={0}>
              <FeedbackItem
                feedback={feedback}
                isAdmin={isAdmin}
                showCourse={showCourse}
                showUnread={showUnread}
                updateFeedbacks={updateData}
              />
            </BoxRow>
          ))
        ) : (
          <EmptyIndicator
            description={emptyDescription || messages.feedbackEmptyDescription}
            icon="bullhorn"
            title={messages.feedbackEmpty}
          />
        )}
      </Box>

      <Pagination
        currentPage={page}
        numberOfPages={data?.pages || 1}
        onPageChange={({ page }) => setPage(page)}
      />
    </div>
  );
};

export default FeedbackContainer;
